import type { Creator } from "../types/Creator";
import { DEFAULT_CREATOR } from "@/constants";

/**
 * Identify creator from current URL or domain
 * Supports both path-based and subdomain-based routing
 */
export function identifyCreator(): string | null {
  const url = window.location.href;
  //const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // Check for subdomain-based routing (e.g., trench-crusade.only-games.co)
  // if (hostname.includes(".")) {
  //   const subdomain = hostname.split(".")[0];
  //   if (
  //     subdomain &&
  //     subdomain !== "localhost" &&
  //     subdomain !== "www" &&
  //     subdomain !== "127"
  //   ) {
  //     return subdomain;
  //   }
  // }

  const pathSegments = pathname.split("/").filter(Boolean);
  if (pathSegments.length > 0) {
    const creatorId = pathSegments[0];
    return creatorId;
  }

  return null;
}

/**
 * Extract query parameters from current URL
 */
export function getUrlParams(): Record<string, string> | null {
  const search = window.location.search;
  if (!search || search === "?") {
    return null;
  }

  const params = new URLSearchParams(search);
  const entries = Object.fromEntries(params.entries());

  return Object.keys(entries).length > 0 ? entries : null;
}

/**
 * Get creator ID from various sources with fallback
 */
export function getCreatorId(): string | null {
  // First try to get from URL
  const urlCreatorId = identifyCreator();
  if (urlCreatorId) {
    return urlCreatorId;
  }

  // Fallback to localStorage if previously set
  const storedCreatorId = localStorage.getItem("currentCreatorId");
  if (storedCreatorId) {
    // console.log("Creator ID from localStorage:", storedCreatorId);
    return storedCreatorId;
  }

  // Final fallback to default
  return DEFAULT_CREATOR;
}

/**
 * Set creator ID in localStorage for persistence
 */
export function setCreatorId(creatorId: string): void {
  localStorage.setItem("currentCreatorId", creatorId);
}

/**
 * Clear stored creator ID
 */
export function clearCreatorId(): void {
  localStorage.removeItem("currentCreatorId");
}

/**
 * Check if current URL matches a specific creator
 */
export function isCreatorUrl(creatorId: string): boolean {
  const currentCreatorId = identifyCreator();
  return currentCreatorId === creatorId;
}

/**
 * Get the base URL for a creator
 */
export function getCreatorUrl(creatorId: string): string {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    // Local development - use path-based routing
    return `${protocol}//${hostname}${port ? ":" + port : ""}/${creatorId}`;
  } else {
    // Production - use subdomain-based routing
    return `${protocol}//${creatorId}.${hostname}`;
  }
}

/**
 * Load creator data JSON by id
 */
export async function loadCreatorData(
  creatorId: string
): Promise<Creator | null> {
  try {
    const response = await fetch(`/configs/creators/${creatorId}.json`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const creatorData = await response.json();
    return creatorData as Creator;
  } catch (error) {
    console.error(`Failed to load creator data for ${creatorId}:`, error);
    return null;
  }
}

/**
 * Resolve the preferred theme object for a creator id
 */
export async function getCreatorPreferredTheme(
  creatorId: string
): Promise<any | null> {
  try {
    const creator = await loadCreatorData(creatorId);
    if (!creator) return null;

    // Ensure global themes are loaded
    try {
      const {
        loadGlobalThemes,
        getThemeCount,
        getDefaultThemeId,
        getThemeById,
      } = await import("./themeUtils");
      if (getThemeCount() === 0) {
        await loadGlobalThemes();
      }

      const preferredThemeId = creator.settings.theme || getDefaultThemeId();
      return getThemeById(preferredThemeId);
    } catch (error) {
      console.warn("Failed to load themes:", error);
      return null;
    }
  } catch (error) {
    console.warn("Failed to get creator preferred theme:", error);
    return null;
  }
}
